import type { ReactNode } from "react";
import clsx from "clsx";

type PageHeroStat = {
  label: ReactNode;
  value: ReactNode;
  description?: ReactNode;
};

type PageHeroProps = {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  stats?: PageHeroStat[];
  className?: string;
  contentClassName?: string;
  statsClassName?: string;
  statClassName?: string;
  centered?: boolean;
};

const PageHero = ({
  eyebrow,
  title,
  description,
  footer,
  stats,
  className,
  contentClassName,
  statsClassName,
  statClassName,
  centered = true,
}: PageHeroProps) => (
  <section
    className={clsx(
      "overflow-hidden rounded-[1.75rem] border border-myteengig-border bg-[radial-gradient(circle_at_top_left,_rgba(191,239,255,0.45),_transparent_28%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(234,248,255,0.92))] px-4 py-6 shadow-sm sm:px-6 sm:py-8 lg:px-8 lg:py-10",
      className,
    )}
  >
    <div className={clsx("mx-auto max-w-3xl", centered && "text-center", contentClassName)}>
      {eyebrow ? (
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-myteengig-primary">{eyebrow}</p>
      ) : null}
      <h1 className="mt-3 text-3xl font-bold tracking-tight text-myteengig-text-primary sm:text-4xl">{title}</h1>
      {description ? <p className="mt-3 text-sm leading-6 text-myteengig-text-muted sm:text-base sm:leading-7">{description}</p> : null}
      {footer ? <div className="mt-5">{footer}</div> : null}
    </div>

    {stats?.length ? (
      <div
        className={clsx(
          "mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4",
          centered && "mx-auto max-w-5xl",
          statsClassName,
        )}
      >
        {stats.map((stat, index) => (
          <div
            key={index}
            className={clsx(
              "rounded-[1.5rem] border border-myteengig-border bg-white px-4 py-4 text-center sm:px-5",
              statClassName,
            )}
          >
            <p className="text-xs uppercase tracking-[0.18em] text-myteengig-text-muted">{stat.label}</p>
            <div className="mt-2 text-2xl font-bold text-myteengig-text-primary sm:text-3xl">{stat.value}</div>
            {stat.description ? (
              <p className="mt-2 text-sm font-normal leading-6 text-myteengig-text-muted">{stat.description}</p>
            ) : null}
          </div>
        ))}
      </div>
    ) : null}
  </section>
);

export default PageHero;
